import { CaseReducer, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	InternetVerdict,
	PingSample,
	SAMPLE_WINDOW,
} from "../../utils/connectionQuality";

interface ExternalCheck {
	verdict: InternetVerdict;
	checkedAt: number;
}

interface State {
	online: boolean;
	/** Last pings, oldest first; `null` = lost. */
	samples: PingSample[];
	lastCheckAt?: number;
	/** PRPC reconnects since the page was loaded. */
	reconnects: number;
	/** External IP as Cloudflare sees it. */
	externalIp?: string;
	external?: ExternalCheck;
}

type Reducer<Payload> = CaseReducer<State, PayloadAction<Payload>>;

const setOnline: Reducer<State["online"]> = (state, { payload }) => {
	state.online = payload;
};

const addSample: Reducer<{ sample: PingSample; at: number }> = (
	state,
	{ payload },
) => {
	state.samples = [...state.samples, payload.sample].slice(-SAMPLE_WINDOW);
	state.lastCheckAt = payload.at;
	// An answered ping proves the network is there, whatever
	// `navigator.onLine` says (it can be wrong with VPNs, virtual adapters).
	if (payload.sample !== null) state.online = true;
};

const resetSamples: Reducer<void> = (state) => {
	state.samples = [];
};

const addReconnect: Reducer<void> = (state) => {
	state.reconnects += 1;
};

const setExternalIp: Reducer<State["externalIp"]> = (state, { payload }) => {
	state.externalIp = payload;
};

const setExternal: Reducer<ExternalCheck> = (state, { payload }) => {
	state.external = payload;
};

const initialState: State = {
	online: typeof navigator === "undefined" ? true : navigator.onLine,
	samples: [],
	reconnects: 0,
};

const connectionQuality = createSlice({
	name: "connectionQuality",
	initialState,
	reducers: {
		setOnline,
		addSample,
		resetSamples,
		addReconnect,
		setExternalIp,
		setExternal,
	},
});

export default connectionQuality;
